import { useState } from "react";
import { Link, useLocation as useRouterLocation } from "react-router-dom";
import { MapPin, ChevronDown, Globe, ShoppingBag, AlertTriangle } from "lucide-react";
import { useLocation, LOCATIONS } from "../../context/LocationContext";
import { useLanguage } from "../../context/LanguageContext";

export const TopNav = () => {
  const routerLocation = useRouterLocation();
  const { location, locationData, updateLocation, resetOnboarding } = useLocation();
  const { language, setLanguage } = useLanguage();
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  
  const navItems = [
    { path: "/", label: { en: "Home", it: "Home", fr: "Accueil", de: "Start", es: "Inicio" } },
    { path: "/concierge", label: { en: "Ask Lina", it: "Chiedi a Lina", fr: "Demander à Lina", de: "Frag Lina", es: "Pregunta a Lina" } },
    { path: "/beaches", label: { en: "Beaches", it: "Spiagge", fr: "Plages", de: "Strände", es: "Playas" } },
    { path: "/events", label: { en: "Events", it: "Eventi", fr: "Événements", de: "Events", es: "Eventos" } },
    { path: "/upsells", label: { en: "Extras", it: "Extra", fr: "Extras", de: "Extras", es: "Extras" } },
  ];

  const languages = [
    { code: "en", label: "EN" },
    { code: "it", label: "IT" },
    { code: "fr", label: "FR" },
    { code: "de", label: "DE" },
    { code: "es", label: "ES" }
  ];

  return (
    <nav className="bg-sand-100 border-b border-sand-300 px-6 py-3 sticky top-0 z-50 backdrop-blur-sm bg-sand-100/95" data-testid="top-nav">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo">
          <div className="w-8 h-8 bg-gradient-to-br from-olive-500 to-olive-600 rounded-full flex items-center justify-center shadow-md shadow-olive-600/20">
            <span className="text-white font-bold text-sm">O</span>
          </div>
          <span className="text-xl font-semibold text-stone-800">OspitAI</span>
        </Link>
        
        {/* Location Selector */}
        <div className="relative">
          <button
            onClick={() => setShowLocationDropdown(!showLocationDropdown)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-olive-100 text-olive-700 hover:bg-olive-200 transition-colors text-sm font-medium"
            data-testid="location-selector"
          >
            <MapPin size={14} />
            <span>{locationData?.name || "Select location"}</span>
            <ChevronDown size={14} />
          </button>
          
          {showLocationDropdown && (
            <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-lg border border-sand-300 py-2 min-w-[180px] z-50">
              {Object.values(LOCATIONS).map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => {
                    updateLocation(loc.id);
                    setShowLocationDropdown(false);
                  }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-sand-100 flex items-center justify-between ${
                    location === loc.id ? "text-olive-700 font-medium" : "text-stone-700"
                  }`}
                >
                  <span>{loc.name}</span>
                  {location === loc.id && <span className="text-olive-600">✓</span>}
                </button>
              ))}
              <div className="border-t border-sand-200 mt-2 pt-2 px-4">
                <button
                  onClick={() => {
                    resetOnboarding();
                    setShowLocationDropdown(false);
                  }}
                  className="text-xs text-stone-500 hover:text-stone-700"
                >
                  Change preferences
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => {
            const isActive = routerLocation.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.en.toLowerCase().replace(' ', '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isActive 
                    ? "bg-olive-600 text-white shadow-md shadow-olive-600/20" 
                    : "text-stone-600 hover:text-stone-900 hover:bg-sand-200"
                }`}
              >
                {item.label[language] || item.label.en}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          {/* Emergency Link */}
          <Link
            to="/emergency"
            className="hidden sm:flex items-center gap-1.5 text-terracotta-600 hover:text-terracotta-700 text-sm font-medium"
            data-testid="emergency-link"
          >
            <AlertTriangle size={14} />
            <span className="hidden lg:inline">{language === 'it' ? 'Emergenza' : language === 'fr' ? 'Urgence' : language === 'de' ? 'Notfall' : language === 'es' ? 'Emergencia' : 'Emergency'}</span>
          </Link>

          {/* Language Selector */}
          <div className="relative">
            <button
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-stone-600 hover:bg-sand-200 transition-colors text-sm"
            >
              <Globe size={14} />
              <span>{language.toUpperCase()}</span>
            </button>
            
            {showLangDropdown && (
              <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-lg border border-sand-300 py-1 min-w-[80px] z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setShowLangDropdown(false);
                    }}
                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-sand-100 ${
                      language === lang.code ? "text-olive-700 font-medium" : "text-stone-700"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/host-login"
            className="hidden sm:flex bg-sea-700 hover:bg-sea-800 text-white px-4 py-2 rounded-full text-sm font-medium transition-colors shadow-md shadow-sea-700/20"
            data-testid="host-login-btn"
          >
                        {language === 'it' ? 'Area Host' : language === 'fr' ? 'Espace Hôte' : language === 'de' ? 'Host-Bereich' : language === 'es' ? 'Área Host' : 'Host Area'}
          </Link>
        </div>
      </div>

      {/* Close dropdowns when clicking outside */}
      {(showLocationDropdown || showLangDropdown) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowLocationDropdown(false);
            setShowLangDropdown(false);
          }}
        />
      )}
    </nav>
  );
};
